import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { UserDetailResponse } from '@edtech-ui/core/models/Classroom/user-detail';
import { StudentMyLearningEnrolledCourses } from '@edtech-ui/core/models/student-courses-v2/student-enroll-my-courses';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { StudentCoursesService } from '@edtech-ui/core/services/student-courses/student-courses.service';
import { SharedModule } from '@edtech-ui/shared/shared.module';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, forkJoin, of, Subscription, tap } from 'rxjs';
import { PaymentService } from '@edtech-ui/core/services/payment-management/payment.service';
import { CurrentSubscription, StudentDataPackageProduct } from '@edtech-ui/core/models/student-courses-v2/student-products-lists';
import { StudentPaymentActivePayload } from '@edtech-ui/core/models/student-courses-v2/student-payment-activate';
import { environment } from 'environments/environment';
import { StudentEnrollmentStatus } from '@edtech-ui/constants/student-course-enrollment.enum';
import { TeacherServicesService } from '@edtech-ui/core/services/teacher-services.service';
import { UsageLimit } from '@edtech-ui/core/models/student-courses-v2/student-limit-stats';
import { StudentsSubscriptionPlan } from '@edtech-ui/constants/current-subscription.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { FreeTrialDialogBoxComponent } from '../free-trial-dialog-box/free-trial-dialog-box.component';
import { normalizeTimeZone } from '@edtech-ui/core/utils/date-time.util';
import { StudentJobStudentApplication } from '@edtech-ui/core/models/student-job-management/get-all-my-applications';
import { StudentJobService } from '@edtech-ui/core/services/student-job-management/student-job.service';
import { GetAllStudentJobs } from '@edtech-ui/core/models/student-job-management/get-all-student-jobs';
import { FeaturedMockTestComponent } from '@edtech-ui/b2c-landing-page/new-components/featured-mock-test/featured-mock-test.component';
import { FeaturedMockInterviewComponent } from '@edtech-ui/b2c-landing-page/new-components/featured-mock-interview/featured-mock-interview.component';

@Component({
	selector: 'app-home',
	standalone: true,
	imports: [SharedModule, MatTooltipModule, MatProgressSpinnerModule, RouterModule,
		HasPermissionDirective, FeaturedMockTestComponent, FeaturedMockInterviewComponent],
	templateUrl: './home.component.html',
	styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, OnDestroy {

	searchQuery = '';
	userId = '';
	isLoading: boolean = false;
	isStatsLoading: boolean = false;
	isCourseLoading = false;
	classId = environment.classroomId;
	subscription = new Subscription();
	myCourses: StudentMyLearningEnrolledCourses[] = [];
	userDetail: UserDetailResponse['Data'] | null = null;
	studentPackages: StudentDataPackageProduct[] = [];
	studentPackage!: StudentDataPackageProduct;
	currentSubscription!: CurrentSubscription;
	productId!: string;
	priceId!: string;
	StudentEnrollmentStatus = StudentEnrollmentStatus;
	students: UsageLimit[] = [];
	limitOverFeature: any;
	showRemainingOnly = false;
	showRemainingQuizOnly = false;
	isFreeTrial = false;
	freeTrialTimeoutId: any;
	StudentsSubscriptionPlan = StudentsSubscriptionPlan;
	PermissionForStudents = PermissionForStudents;
	hasLimitOver: boolean = false;
	completedFeatures: string[] = [];
	pendingFeatures: string[] = [];
	upgradeFeatures: string[] = [];
	jobData: StudentJobStudentApplication[] = [];
	total: number = 0;
	totalPages: number = 0;
	page: number = 1;
	limit: number = 10;
	isApplicationLoading: boolean = false;
	recommendedJobs: GetAllStudentJobs[] = [];
	isRecommendedJobsLoading: boolean = false;
	userPreferences: any = {};
	isJobSave: boolean = false;
	isJobRemove: boolean = false;

	constructor(
		private _authService: AuthServiceService,
		private _toastr: ToastrService,
		private router: Router,
		private _studentCourseService: StudentCoursesService,
		private dialog: MatDialog,
		private _paymentService: PaymentService,
		private _teacherService: TeacherServicesService,
		private studentJobService: StudentJobService
	) { }

	ngOnInit(): void {
		const storedId = localStorage.getItem('user_id');
		if (storedId) {
			this.userId = storedId;
			this.getUserInfo(this.userId);
		}
		if (this._authService.hasPermission(PermissionForStudents.DASHBOARD_STUDENT)) {
			this.getStudentDashboardLimits();
		}

		if (this._authService.hasPermission(PermissionForStudents.COURSE_VIEW)) {
			this.getMyEnrollCourses();
		}

		if (this._authService.hasPermission(PermissionForStudents.PRODUCT_PACKAGES)) {
			this.getStudentProductData();
		}

		if (this._authService.hasPermission(PermissionForStudents.JOB_APPLY)) {
			this.getAllStudentApplications();
		}
	}

	@HostListener('document:visibilitychange', ['$event'])
	onVisibilityChange(): void {
		if (document.visibilityState === 'visible') {
			this.refreshDashboardData();
		}
	}

	private refreshDashboardData(): void {
		if (this._authService.hasPermission(PermissionForStudents.JOB_APPLY) && !this.isApplicationLoading) {
			this.page = 1;
			this.getAllStudentApplications();
		}

		if (this._authService.hasPermission(PermissionForStudents.DASHBOARD_STUDENT) && !this.isStatsLoading) {
			this.getStudentDashboardLimits();
		}
	}

	getUserInfo(id: string) {
		this.isLoading = true;
		this.subscription.add(
			this._authService.getUserDetails(id).pipe(
				finalize(() => {
					this.isLoading = false;
				})
			).subscribe({
				next: (response: UserDetailResponse) => {
					if (response.Success) {
						this.userDetail = response.Data;
						localStorage.setItem('country', response.Data.country || 'null');
						localStorage.setItem('user_time_zone', normalizeTimeZone(response.Data.timezone));
						this.userPreferences = {
							preferred_job_role_category: response.Data.preferred_job_role_category,
							preferred_job_title: response.Data.preferred_job_title,
							skills: response.Data.skills
						};
						this.getRecommendedJobs();
					}
				},
				error: () => this._toastr.error('Something went wrong while fecthing the user Details')
			})
		);
	}


	getMyEnrollCourses() {
		this.isCourseLoading = true;
		this.subscription.add(
			this._studentCourseService.getMyEnrollCourses().pipe(
				tap(response => {
					if (response.Success) {
						this.myCourses = response.Data.courses;
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isCourseLoading = false;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong while fetching courses');
					return of(null);
				})
			).subscribe()
		);
	}

	goToCourseDescriptionPage(courseId: string) {
		this.router.navigate(['/student-dashboard/course-detail-page', this.classId, courseId])
	}



	getStudentProductData() {
		this.subscription.add(
			this._paymentService.getStudentProducts().pipe(
				tap(response => {
					if (response.Success) {
						this.studentPackages = response.Data.products;
						this.currentSubscription = response.Data.current_subscription;

						this.isFreeTrial = this.currentSubscription?.product_name === StudentsSubscriptionPlan.FREE_TRIAL &&
							this.currentSubscription?.product_price === StudentsSubscriptionPlan.PRICE_VALUE;


						if (this.currentSubscription &&
							this.currentSubscription.product_name === null &&
							this.currentSubscription.product_price === StudentsSubscriptionPlan.PRICE_VALUE) {
							const freeTrialPackage = this.studentPackages.find(p => p.price === StudentsSubscriptionPlan.PRICE_VALUE);
							if (freeTrialPackage) {

								// if (this._authService.hasPermission(PermissionForStudents.INTERVIEW_VIEW)) {
								// 	this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
								// } else {

								// 	this.router.navigate(['/student-dashboard/home'])
								// }
								this.activateFreeTrial(freeTrialPackage.product_id);
							}
						}

						const hasPurchased = response.Data.products.some(p => p.has_purchased);
						const hasPaidSubscription = this.currentSubscription?.product_name !== null &&
							this.currentSubscription?.product_name !== undefined &&
							this.currentSubscription?.product_name !== StudentsSubscriptionPlan.FREE_TRIAL;

						if (!hasPurchased && !hasPaidSubscription) {
							this.handleFreeTrialDialog();
						}
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong .Please try again');
					return of(null);
				})
			).subscribe()
		);
	}

	activateFreeTrial(productId: string) {
		const payload: StudentPaymentActivePayload = {
			product_id: productId
		}
		this._paymentService.postPaymentActiveSubscription(payload).pipe(
			tap(response => {
				if (response.Success) {
					this._toastr.success('Free Trial activated. Explore with limited access');
				} else {
					this._toastr.error(response.Message);
				}
			}),
			catchError(() => {
				this._toastr.error('Something went wrong. Please try again');
				return of(null);
			}),
		).subscribe();
	}

	getStudentDashboardLimits() {
		this.isStatsLoading = true;

		forkJoin({
			limits: this._teacherService.getStudentDashboardLimit().pipe(catchError(err => of(null))),
			packages: this._paymentService.getStudentProducts().pipe(catchError(err => of(null)))
		}).pipe(
			tap(({ limits, packages }) => {

				if (!limits || !limits.Data || !packages || !packages.Data) {
					this._toastr.error('Failed to fetch dashboard data.');
					return;
				}

				this.limitOverFeature = packages.Data.current_subscription.limit_over_features || [];
				this.hasLimitOver = !!packages.Data.current_subscription.limit_over;
				this.currentSubscription = packages.Data.current_subscription;
				this.checkLimitFeatures();
				this.students = [
					//{
					//	title: 'Courses Accessed',
					//	used: limits.Data.professional_courses.used,
					//	total: limits.Data.professional_courses.total,
					//	remaining: limits.Data.professional_courses.remaining,
					//	label: limits.Data.professional_courses.label,
					//	text: limits.Data.professional_courses.label,
					//	subtext: 'Courses',
					//	icon: 'menu_book',
					//	color: 'blue',
					//	route: '/student-dashboard/my-learning',
					//	permission: PermissionForStudents.COURSE_VIEW
					//},
					{
						title: 'AI Mock Test',
						used: limits.Data.ai_mock_tests.used,
						total: limits.Data.ai_mock_tests.total,
						remaining: limits.Data.ai_mock_tests.remaining,
						label: limits.Data.ai_mock_tests.label,
						text: limits.Data.ai_mock_tests.label,
						subtext: 'Question',
						icon: 'assignment',
						color: 'green',
						route: '/student-dashboard/placement-prep',
						permission: PermissionForStudents.PLACEMENT_VIEW
					},
					{
						title: 'AI Interview Practise',
						used: limits.Data.ai_interview_minutes.used,
						total: limits.Data.ai_interview_minutes.total,
						remaining: limits.Data.ai_interview_minutes.remaining,
						label: limits.Data.ai_interview_minutes.label,
						text: limits.Data.ai_interview_minutes.label,
						subtext: 'Minutes',
						icon: 'psychology',
						color: 'red',
						route: '/student-dashboard/ai-interview-dashboard',
						permission: PermissionForStudents.INTERVIEW_VIEW
					},
					//{
					//	title: 'Quiz Attempted',
					//	used: limits.Data.quizzes_completed.used,
					//	total: limits.Data.quizzes_completed.total,
					//	remaining: limits.Data.quizzes_completed.remaining,
					//	label: limits.Data.quizzes_completed.label,
					//	text: limits.Data.quizzes_completed.label,
					//	subtext: 'Quiz',
					//	icon: 'assignment_turned_in',
					//	color: 'yellow',
					//	route: '/student-dashboard/my-learning',
					//	showRemainingQuizOnly: true,
					//	permission: PermissionForStudents.QUIZ_VIEW
					//},
					//{
					//	title: 'Mentorship Booked',
					//	used: limits.Data.mentorship_sessions.used,
					//	total: limits.Data.mentorship_sessions.total,
					//	remaining: limits.Data.mentorship_sessions.remaining,
					//	label: limits.Data.mentorship_sessions.label,
					//	text: limits.Data.mentorship_sessions.label,
					//	subtext: '',
					//	icon: 'co_present',
					//	color: 'purple',
					//	route: '/student-dashboard/my-sessions',
					//	showRemainingOnly: true,
					//	permission: PermissionForStudents.MENTOR_VIEW
					//},
				]
			}),
			finalize(() => {
				this.isStatsLoading = false;
			})
		).subscribe()
	}

	navigateTo(studentData: any) {
		if (this.isFreeTrial) {
			this.router.navigate(['/student-dashboard/upgrade-plan']);
			return;
		}

		if (studentData?.title === 'AI Mock Test') {
			this.router.navigate(['/student-dashboard/placement-prep'], { queryParams: { tab: 'history' } });
			return;
		}
		if (studentData?.route) {
			this.router.navigate([studentData.route]);
		}
		if (studentData?.title === 'Quiz Attempted') {
			this.router.navigate(['/student-dashboard/my-learning'], { queryParams: { tab: 'quiz-completed' } });
			return;
		}
	}

	getProgressPercent(studentData: { used: number; total: number | null }): number {
		if (!studentData.total || studentData.total === 0) return 0;
		return Math.min((studentData.used / studentData.total) * 100, 100);
	}

	getProgressColor(studentData: { used: number; total: number | null }): string {
		const percent = this.getProgressPercent(studentData);

		if (percent === 0) return '';
		return 'Bright Gray';
	}

	featureUpgradeMap: Record<string, string[]> = {
		'Professional Courses': ['Professional Courses'],
		'Placement Prep': ['AI Mock Test'],
		'AI Interview Prep': ['AI Interview Practice'],
		'Mentor Session': ['Mentor Session'],
	};


	allPracticeFeatures = [
		'AI Mock Test',
		'AI Interview Practice'
	];

	featureKeyMap: Record<string, string> = {
		'AI Interview Prep': 'AI Interview Practice',
		'Placement Prep': 'AI Mock Test'
	};


	handleFreeTrialDialog() {
		const storageKey = 'free_trial_shown';
		const hasShown = sessionStorage.getItem(storageKey);
		if (!hasShown) {
			this.freeTrialTimeoutId = setTimeout(() => {
				const dialogRef = this.dialog.open(FreeTrialDialogBoxComponent, {
					disableClose: false
				});

				dialogRef.afterClosed().subscribe((result) => {
					if (result === 'interview') {
						this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
					} else if (result === 'mock-test') {
						this.router.navigate(['/student-dashboard/placement-prep']);
					}
				});

				sessionStorage.setItem(storageKey, 'true');
			}, 2000);
		}
	}

	checkLimitFeatures(): void {
		this.completedFeatures = (this.currentSubscription?.limit_over_features || [])
			.map((f: string) => this.featureKeyMap[f]).filter(Boolean);
		this.pendingFeatures = this.allPracticeFeatures.filter(f => !this.completedFeatures.includes(f));

		const set = new Set<string>();
		this.limitOverFeature?.forEach((feature: any) => {
			this.featureUpgradeMap[feature]?.forEach(f => set.add(f));
		});
		this.upgradeFeatures = Array.from(set);
	}


	goToInterviewRulesScreen() {
		this.router.navigate(['/student-dashboard/jobs/assessment-rules']);
	}


	goToAssesmentRulesScreen() {
		this.router.navigate(['/student-dashboard/jobs/ai-interview-rules']);
	}


	getAllStudentApplications() {
		this.isApplicationLoading = true;
		this.subscription.add(
			this.studentJobService.getAllStudentApplications(
				this.page,
				this.limit
			).pipe(
				tap(response => {
					if (response.Success) {
						if (this.page === 1) {
							this.jobData = response.Data?.applications || [];
						} else {
							this.jobData = [...this.jobData, ...(response.Data?.applications || [])];
						}
						this.jobData.sort((a, b) => {
							const dateA = a.applied_at ? new Date(a.applied_at).getTime() : 0;
							const dateB = b.applied_at ? new Date(b.applied_at).getTime() : 0;
							return dateB - dateA;
						});
						this.total = response.Data?.total || 0;
						this.page = response.Data?.page || 1;
						this.limit = response.Data?.limit || 10;
						this.totalPages = response.Data?.total_pages || 0;
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isApplicationLoading = false;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong.Please try again');
					return of(null);
				})
			).subscribe()
		)
	}


	openAssessment(application: StudentJobStudentApplication) {
		const assessment = application.assessment;
		if (!assessment?.quiz_link_url) {
			this._toastr.error('Invalid assessment URL');
			return;
		}
		if (assessment.end_date) {
			const endDate = new Date(assessment.end_date);
			const now = new Date();
			if (now >= endDate) {
				this._toastr.error('This assessment link is no longer active', 'Assessment link expired');
				return;
			}
		}
		try {
			const assessmentUrl = new URL(assessment.quiz_link_url);
			assessmentUrl.searchParams.set('isStudentDashboard', 'true');
			window.open(assessmentUrl.toString(), '_blank');
		} catch (error) {
			console.error('Invalid assessment URL:', assessment.quiz_link_url);
			this._toastr.error('Invalid assessment URL');
		}
	}


	openInterview(application: StudentJobStudentApplication) {
		const interview = application.interview;
		if (!interview?.interview_url) {
			this._toastr.error('Invalid interview URL');
			return;
		}
		if (interview.end_date) {
			const endDate = new Date(interview.end_date);
			const now = new Date();
			if (now >= endDate) {
				this._toastr.error('This interview link is no longer active','Interview link expired');
				return;
			}
		}
		try {
			const interviewUrl = new URL(interview.interview_url);
			interviewUrl.searchParams.set('isStudentDashboard', 'true');
			window.open(interviewUrl.toString(), '_blank');
		} catch (error) {
			console.error('Invalid interview URL:', interview.interview_url);
			this._toastr.error('Invalid interview URL');
		}
	}


	showAssessment(application: StudentJobStudentApplication): boolean {
		return application.assessment?.status !== 'not_assigned';
	}


	showInterview(application: StudentJobStudentApplication): boolean {
		return application.interview?.status !== 'not_assigned';
	}


	showAssessmentButton(application: StudentJobStudentApplication): boolean {
		return application.assessment?.status === 'pending';
	}


	showInterviewButton(application: StudentJobStudentApplication): boolean {
		if (application.interview?.status !== 'pending') {
			return false;
		}
		if (!application.assessment || application.assessment.status === 'not_assigned') {
			return true;
		}
		return !!application.assessment.completed_at;
	}


	loadMore() {
		if (this.page < this.totalPages && !this.isLoading) {
			this.page++;
			this.getAllStudentApplications();
		}
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		if (this.freeTrialTimeoutId) {
			clearTimeout(this.freeTrialTimeoutId);
		}
	}

	isAllStepsCompleted(application: StudentJobStudentApplication | null | undefined): boolean {
		if (!application) return false;

		const assessmentAssigned = !!application.assessment && application.assessment.status !== 'not_assigned';
		const interviewAssigned = !!application.interview && application.interview.status !== 'not_assigned';

		if (!assessmentAssigned && !interviewAssigned) {
			return false;
		}

		const assessmentCompleted = !assessmentAssigned || !!application.assessment?.completed_at;
		const interviewCompleted = !interviewAssigned || !!application.interview?.completed_at;

		return assessmentCompleted && interviewCompleted;
	}

	getRecommendedJobs() {
		this.isRecommendedJobsLoading = true;

		if (!this.userPreferences.preferred_job_role_category ||
			!this.userPreferences.preferred_job_title ||
			!this.userPreferences.skills ||
			this.userPreferences.skills.length === 0 ||
			!this.userDetail?.resume_url) {
			this.recommendedJobs = [];
			this.isRecommendedJobsLoading = false;
			return;
		}

		const filters: any = { is_visible: true };
		if (this.userPreferences.preferred_job_role_category) filters.preferred_job_role_category = this.userPreferences.preferred_job_role_category;
		if (this.userPreferences.preferred_job_title) filters.preferred_job_title = this.userPreferences.preferred_job_title;
		if (this.userPreferences.skills && this.userPreferences.skills.length > 0) filters.skills = this.userPreferences.skills;

		this.subscription.add(
			this.studentJobService.getAllStudentsJobs(
				1,
				6,
				'recommended',
				'',
				'',
				filters
			).pipe(
				tap((response: any) => {
					if (response.Success) {
						this.recommendedJobs = (response.Data.jobs || []).map((job: any) => ({
							...job,
							locations: (job.locations || []).filter((location: any) => location?.trim())
						}));
					}
				}),
				finalize(() => {
					this.isRecommendedJobsLoading = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}

	truncateText(text: string, limit: number): string {
		return text?.length > limit ? text.slice(0, limit) + '...' : text;
	}

	getDaysAgo(dateString: string | Date | undefined | null): string | null {
		if (!dateString) return null;

		const publishedDate = new Date(dateString);
		const today = new Date();

		const timeDiff = today.getTime() - publishedDate.getTime();
		const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

		if (daysDiff >= 0 && daysDiff <= 30) {
			if (daysDiff === 0) return 'Posted today';
			if (daysDiff === 1) return 'Posted 1 day ago';
			return `Posted ${daysDiff} days ago`;
		}

		return null;
	}

	toggleSaveJob(job: GetAllStudentJobs) {
		if (job.is_saved) {
			this.deleteStudentSaveJob(job);
		} else {
			this.postStudentSaveJob(job);
		}
	}

	postStudentSaveJob(job: GetAllStudentJobs) {
		this.isJobSave = true;
		this.subscription.add(
			this.studentJobService.postStudentSaveJob(job.job_id).pipe(
				tap((response: any) => {
					if (response.Success) {
						this._toastr.success(response.Data.message);
						this.getRecommendedJobs();
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isJobSave = false;
				})
			).subscribe()
		);
	}

	deleteStudentSaveJob(job: GetAllStudentJobs) {
		this.isJobRemove = true;
		this.subscription.add(
			this.studentJobService.deleteStudentSaveJob(job.job_id).pipe(
				tap((response: any) => {
					if (response.Success) {
						this._toastr.success(response.Data.message);
						this.getRecommendedJobs();
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isJobRemove = false;
				})
			).subscribe()
		);
	}

	goToJobsDetailPage(jobId: string) {
		this.router.navigate(['/student-dashboard/jobs-detail-page', jobId]);
	}

}